import { Icon } from "@core/icons";
import { TableRowActionButton, tableSpacing } from "@core/table";
import type { TableRowActionTone } from "@core/table";
import { Tooltip } from "./Tooltip";

/**
 * Acción de fila en forma de ícono + `Tooltip` (revisión en vivo 2026-09-22,
 * punto #10, fase 4b).
 *
 * El problema que resuelve no es estético: con tres o cuatro acciones de
 * TEXTO por fila, la columna de acciones no alcanza y el grupo hace `wrap`
 * ("Inactivar" caía sola a una segunda fila en el carrusel de Inicio); y
 * cuando una de esas acciones es riesgosa pero se dibuja como texto plano
 * (`.btn-link`), no se distingue de una etiqueta. Un ícono mide siempre lo
 * mismo (40px de caja) sin importar el largo de la etiqueta, y el texto que
 * se pierde vuelve por el tooltip.
 *
 * Reglas que hay que respetar al usarlo, porque son las que hacen que un
 * ícono siga siendo una acción usable:
 *
 *  · `label` es el MISMO texto que mostraba el botón. Va al `aria-label` del
 *    `<button>`, no solo al tooltip: el `aria-describedby` que inyecta
 *    `Tooltip` es una DESCRIPCIÓN, nunca un nombre accesible. Sin el
 *    `aria-label` el botón se anunciaría como "botón" a secas y cualquier
 *    `getByRole("button", { name: … })` dejaría de encontrarlo.
 *  · el `<svg>` de `Icon` no lleva `<title>` ni texto, así que no compite con
 *    el `aria-label`: el nombre del botón es siempre la etiqueta.
 *  · el `onClick` vive en el `<button>`, nunca en el `<span>` del ícono.
 *
 * `rotate` existe para las dos únicas acciones que no tienen glifo propio en
 * el set: "Subir" y "Bajar" reusan `chevdown` (a 180° y a 0°), igual que
 * "Volver a la app" reusaba `arrow` rotado. No se agregaron íconos nuevos
 * para eso a propósito.
 */
export function RowIconAction({
  label,
  icon,
  onClick,
  tone,
  disabled,
  rotate,
  description,
  placement = "top",
}: {
  /** Texto que mostraba el botón. Es a la vez el nombre accesible y el tooltip. */
  label: string;
  /** Nombre del glifo en `ICON_PATHS` (`@core/icons`). */
  icon: string;
  onClick: () => void;
  tone?: TableRowActionTone;
  disabled?: boolean;
  /** Grados de rotación del glifo (solo "Subir"/"Bajar"). */
  rotate?: number;
  /**
   * Aclaración que se suma al tooltip (hoy: por qué la acción está
   * deshabilitada). NO toca el nombre accesible: el botón se sigue llamando
   * `label`, para que el motivo de hoy no rompa el `getByRole` de mañana.
   */
  description?: string;
  placement?: "top" | "bottom";
}) {
  return (
    <Tooltip
      label={description ? `${label} · ${description}` : label}
      placement={placement}
    >
      <TableRowActionButton
        variant="icon"
        onClick={onClick}
        aria-label={label}
        {...(tone ? { tone } : {})}
        {...(disabled === undefined ? {} : { disabled })}
      >
        <Icon
          name={icon}
          size={tableSpacing.iconActionSize}
          // El trazo de 1.5 del set está pensado para 22px; a 16px se
          // adelgaza a 1px y el glifo se ve desvaído junto a un texto de
          // peso 600. 1.8 lo devuelve al peso óptico del resto de la fila.
          stroke={1.8}
          {...(rotate ? { style: { transform: `rotate(${rotate}deg)` } } : {})}
        />
      </TableRowActionButton>
    </Tooltip>
  );
}
